<template>
  <div>
    <b-sidebar
      id="sidebar-save-financial-adjustment"
      sidebar-class="sidebar-lg"
      data-testid="save-financial-adjustment-sidebar"
      :visible="saveFinancialAdjustmentSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @shown="onShow"
      @change="(val) => mutateSaveFinancialAdjustmentSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4>Ajuste financeiro</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="onSubmit"
          @reset.prevent="resetForm"
        >
          <b-row>
            <b-col cols="12">
              <label for="name">Descrição</label>
              <b-form-input
                id="name"
                v-model="name"
                type="text"
                data-testid="adjustment-name"
                :class="{ 'is-invalid': v$.name.$error }"
              />
              <div class="invalid-feedback">
                <span v-if="v$.name.required.$invalid">
                  Você deve informar uma descrição para este lançamento
                </span>
              </div>
            </b-col>
            <b-col cols="12" class="mt-2">
              <b-form-group label="Valor" label-for="amount">
                <b-form-input
                  id="amount"
                  v-money="moneyFormat"
                  v-model="amount"
                  :class="{ 'is-invalid': v$.amount.$error }"
                />
                <div class="invalid-feedback">
                  <span
                    v-if="
                      v$.amount.required.$invalid || v$.amount.maxValue.$invalid
                    "
                  >
                    Você deve informar um valor válido
                  </span>
                </div>
              </b-form-group>
            </b-col>
            <b-col cols="12" class="mt-2">
              <b-form-group label="Data do lançamento" label-for="launch-date">
                <b-form-input
                  id="launch-date"
                  v-model="launchDate"
                  type="date"
                  :class="{ 'is-invalid': v$.launchDate.$error }"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.launchDate.required.$invalid">
                    Você deve informar a data do lançamento
                  </span>
                </div>
              </b-form-group>
            </b-col>
            <b-col cols="12" class="mt-2">
              <dynamic-select
                id="consultant"
                label="Quem receberá o lançamento?"
                placeholder="Digite o nome..."
                v-model="consultant"
                :options="consultants"
                :loading="loading.consultants"
                @find="findConsultants"
                :thereIsError="v$.consultant.$error"
                errorMessage="Você deve informar quem receberá o lançamento"
                @input="consultantChanged"
              />
            </b-col>
            <b-col cols="12" class="mt-2">
              <b-form-group label="Tipo" label-for="type">
                <v-select
                  id="type"
                  data-testid="adjustment-type"
                  v-model="type"
                  :reduce="(type) => type.key"
                  :options="types"
                  :loading="loading.types"
                  label="name"
                  :class="getSelectErrorClass(v$.type.$error)"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.type.required.$invalid">
                    Você deve informar o tipo do lançamento
                  </span>
                </div>
              </b-form-group>
            </b-col>
            <b-col cols="12" class="mt-2">
              <b-form-group label="Aplicação do valor" label-for="credit-type">
                <v-select
                  id="credit-type"
                  v-model="creditType"
                  :reduce="(creditType) => creditType.key"
                  :options="creditTypes"
                  :loading="loading.creditTypes"
                  label="name"
                  :class="getSelectErrorClass(v$.creditType.$error)"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.creditType.required.$invalid">
                    Você deve informar a forma de aplicação do lançamento
                  </span>
                </div>
              </b-form-group>
            </b-col>
            <b-col cols="12" class="mt-2">
              <b-form-group label="Motivo" label-for="reason">
                <v-select
                  id="reason"
                  data-testid="adjustment-reason"
                  v-model="reason"
                  :reduce="(reason) => reason.id"
                  :options="reasons"
                  :loading="loading.reasons"
                  label="name"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" class="mt-2">
              <b-form-group label="Centro de custo" label-for="cost-center">
                <v-select
                  id="cost-center"
                  data-testid="adjustment-cost-center"
                  v-model="costCenter"
                  :reduce="(cost_center) => cost_center.id"
                  :options="costCenters"
                  :loading="loading.costCenters"
                  @input="costCenterChanged"
                  label="name"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" class="mt-2" v-if="costCenter">
              <b-form-group
                label="Categoria do centro de custo"
                label-for="cost-center-category"
              >
                <v-select
                  id="cost-center-category"
                  data-testid="adjustment-cost-center-category"
                  v-model="costCenterCategory"
                  :reduce="(category) => category.id"
                  :options="costCenterCategories"
                  :loading="loading.costCenterCategories"
                  label="name"
                  :class="getSelectErrorClass(v$.costCenterCategory.$error)"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.costCenterCategory.required.$invalid">
                    Você deve informar a categoria do centro de custo
                  </span>
                </div>
              </b-form-group>
            </b-col>
            <!--
              Empresa pagadora. Ou o Financeiro escolhe o CNPJ na mão (lançamento avulso), ou vincula
              contratos e o CNPJ passa a ser DERIVADO deles. Nunca os dois ao mesmo tempo: com
              contrato vinculado, deixar o campo editável permitiria carimbar "Plataforma" num ajuste
              de solução financeira, e a nota da Hub sairia com valor da outra empresa dentro.
            -->
            <b-col cols="12" class="mt-2">
              <b-form-group
                label="Competência de referência"
                label-for="reference-competence"
              >
                <b-form-input
                  id="reference-competence"
                  v-model="referenceCompetence"
                  type="month"
                  data-testid="adjustment-reference-competence"
                />
                <small class="text-muted">
                  O mês que este lançamento corrige. Não decide em qual fatura ele
                  entra — quem decide isso é a data do lançamento.
                </small>
              </b-form-group>
            </b-col>
            <b-col cols="12" class="mt-2">
              <b-form-group
                label="Empresa pagadora"
                label-for="paying-company-scope"
              >
                <b-form-radio-group
                  id="paying-company-scope"
                  v-model="payingCompanyScope"
                  stacked
                  data-testid="adjustment-paying-company-scope"
                  @change="payingCompanyScopeChanged"
                >
                  <b-form-radio
                    :value="companyScope"
                    data-testid="adjustment-scope-company"
                  >
                    Contra o CNPJ (lançamento avulso)
                  </b-form-radio>
                  <b-form-radio
                    :value="contractScope"
                    data-testid="adjustment-scope-contracts"
                  >
                    Contra contratos
                  </b-form-radio>
                </b-form-radio-group>
              </b-form-group>
            </b-col>
            <b-col v-if="!isContractScope" cols="12">
              <v-select
                id="paying-company"
                v-model="payingCompany"
                :reduce="(company) => company.id"
                :options="payingCompanyOptions"
                label="label"
                data-testid="adjustment-paying-company"
              >
                <template #no-options="{ search, searching, loading }">
                  Sem resultados
                </template>
              </v-select>
              <!--
                O aviso mais importante desta tela: escolher Hub MUDA o valor da nota fiscal que o
                PN vai emitir. Escolher Plataforma nao muda — a Plataforma paga sem exigir nota.
                Sem isso, o operador escolhe o CNPJ como se fosse so uma etiqueta contabil.
              -->
              <small class="text-muted">
                <strong>Futuro Hub</strong> entra no valor da nota fiscal que o PN emite.
                <strong>Futuro Plataforma</strong> nao entra: a Plataforma paga sem exigir nota.
              </small>
            </b-col>
            <template v-else>
              <b-col cols="12">
                <b-form-group
                  label="CNPJ derivado dos contratos"
                  label-for="derived-paying-company"
                >
                  <b-form-input
                    id="derived-paying-company"
                    :value="derivedPayingCompanyLabel"
                    readonly
                    data-testid="adjustment-derived-paying-company"
                  />
                  <b-badge
                    v-if="lockedPayingCompanyId"
                    :variant="payingCompanyVariant(lockedPayingCompanyId)"
                    class="mt-50"
                    data-testid="adjustment-paying-company-lock"
                  >
                    Travado — só entram contratos deste CNPJ
                  </b-badge>
                </b-form-group>
              </b-col>
              <b-col cols="12" class="mt-1">
                <b-form-group
                  label="Colar IDs de contrato"
                  label-for="contract-ids-paste"
                >
                  <b-form-textarea
                    id="contract-ids-paste"
                    v-model="contractIdsInput"
                    rows="2"
                    placeholder="18271, 19177"
                    data-testid="adjustment-contract-ids-input"
                  />
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="mt-1"
                    :disabled="loading.contracts || !contractIdsInput"
                    data-testid="adjustment-contract-ids-validate"
                    @click="validatePastedContractIds"
                  >
                    <b-spinner v-if="loading.contracts" small />
                    {{ loading.contracts ? "Validando..." : "Validar e vincular" }}
                  </b-button>
                </b-form-group>
              </b-col>
              <b-col v-if="pasteFeedback" cols="12">
                <div data-testid="adjustment-contract-paste-feedback">
                  <div
                    v-if="pasteFeedback.linked.length"
                    class="text-success"
                    data-testid="adjustment-contract-paste-linked"
                  >
                    Vinculados: {{ pasteFeedback.linked.join(", ") }}
                  </div>
                  <div
                    v-if="pasteFeedback.duplicated.length"
                    class="text-muted"
                    data-testid="adjustment-contract-paste-duplicated"
                  >
                    Já estavam na lista: {{ pasteFeedback.duplicated.join(", ") }}
                  </div>
                  <div
                    v-if="pasteFeedback.missing.length"
                    class="text-danger"
                    data-testid="adjustment-contract-paste-missing"
                  >
                    Não encontrados: {{ pasteFeedback.missing.join(", ") }}
                  </div>
                  <div
                    v-if="pasteFeedback.otherCompany.length"
                    class="text-warning"
                    data-testid="adjustment-contract-paste-other-company"
                  >
                    De outro CNPJ, recusados:
                    {{ pasteFeedback.otherCompany.join(", ") }} — faça um
                    lançamento separado para eles.
                  </div>
                  <div
                    v-if="pasteFeedback.failed.length"
                    class="text-danger"
                    data-testid="adjustment-contract-paste-failed"
                  >
                    Não foi possível consultar:
                    {{ pasteFeedback.failed.join(", ") }}
                  </div>
                </div>
              </b-col>
              <b-col cols="12" class="mt-1">
                <b-form-group
                  label="Buscar contrato"
                  label-for="contract-search-term"
                >
                  <b-input-group>
                    <b-form-input
                      id="contract-search-term"
                      v-model="contractSearchTerm"
                      placeholder="ID do contrato ou CPF do cliente"
                      data-testid="adjustment-contract-search-term"
                      @keyup.enter="searchContracts"
                    />
                    <b-input-group-append>
                      <b-button
                        variant="outline-secondary"
                        :disabled="loading.contractSearch || !contractSearchTerm"
                        data-testid="adjustment-contract-search-button"
                        @click="searchContracts"
                      >
                        <b-spinner v-if="loading.contractSearch" small />
                        <feather-icon v-else icon="SearchIcon" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col v-if="contractSearchResults.length" cols="12">
                <b-list-group data-testid="adjustment-contract-search-results">
                  <b-list-group-item
                    v-for="result in contractSearchResults"
                    :key="result.id"
                    class="d-flex justify-content-between align-items-center"
                  >
                    <div class="text-wrap mr-1">
                      <div>{{ result.label }}</div>
                      <b-badge
                        :variant="payingCompanyVariant(result.paying_company_id)"
                      >
                        {{ payingCompanyName(result.paying_company_id) }}
                      </b-badge>
                    </div>
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      :data-testid="`adjustment-contract-add-${result.id}`"
                      @click="addContract(result)"
                    >
                      Vincular
                    </b-button>
                  </b-list-group-item>
                </b-list-group>
              </b-col>
              <b-col v-if="contracts.length" cols="12" class="mt-1">
                <label>Contratos vinculados ({{ contracts.length }})</label>
                <b-list-group data-testid="adjustment-linked-contracts">
                  <b-list-group-item
                    v-for="contract in contracts"
                    :key="contract.id"
                    class="d-flex justify-content-between align-items-center"
                    :data-testid="`adjustment-linked-contract-${contract.id}`"
                  >
                    <span class="text-wrap mr-1">{{ contract.label }}</span>
                    <b-button
                      size="sm"
                      variant="flat-danger"
                      class="btn-icon"
                      :data-testid="`adjustment-contract-remove-${contract.id}`"
                      @click="removeContract(contract.id)"
                    >
                      <feather-icon icon="XIcon" />
                    </b-button>
                  </b-list-group-item>
                </b-list-group>
              </b-col>
            </template>
            <b-col v-if="!isEdit" cols="12" class="mt-2">
              <b-form-checkbox
                :checked="hasRecurrence"
                v-model="hasRecurrence"
                switch
                inline
              >
                Haverá recorrência deste lançamento
              </b-form-checkbox>
            </b-col>
            <b-col v-if="!isEdit && hasRecurrence" cols="12" class="mt-2">
              <label for="recurrenceInMonths">Recorrência (em meses)</label>
              <b-form-input
                v-model="recurrenceInMonths"
                type="number"
                :class="{ 'is-invalid': v$.recurrenceInMonths.$error }"
              />
              <div class="invalid-feedback">
                <span
                  v-if="
                    v$.recurrenceInMonths.required.$invalid ||
                    v$.recurrenceInMonths.between.$invalid
                  "
                >
                  A recorrência suporta valores de 1 à 12 meses
                </span>
              </div>
            </b-col>
            <b-col cols="12" class="mt-2">
              <label for="comment">Comentário</label>
              <b-form-textarea
                id="comment"
                v-model="comment"
                class="mb-2 mb-lg-0"
              />
            </b-col>
          </b-row>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              :disabled="saving"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
              data-testid="adjustment-submit"
            >
              {{ saving ? "Salvando..." : "Salvar" }}
            </b-button>
          </div>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BButton,
  BFormTextarea,
  BCol,
  BRow,
  BFormInput,
  BFormRadioGroup,
  BFormRadio,
  BBadge,
  BSpinner,
  BInputGroup,
  BInputGroupAppend,
  BListGroup,
  BListGroupItem,
} from "bootstrap-vue";
import { mapMutations, mapGetters, mapActions } from "vuex";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import useVuelidate from "@vuelidate/core";
import { required, between, requiredIf } from "@vuelidate/validators";
import { useToast } from "vue-toastification/composition";
import { floatToMoney, moneyToFloat } from "@/helpers/converters";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import DynamicSelect from "@/modules/shared/components/DynamicSelect";
import MoneyInput from "@/modules/shared/components/MoneyInput";
import { VMoney } from "v-money";
import { getVueSelectErrorClass } from "@/helpers/validators";
import { getBRLFormat } from "@/helpers/formatting";
import {
  PAYING_COMPANY_OPTIONS,
  PLATFORM_ID,
  payingCompanyFromFinancialSolutionId,
  payingCompanyLabel,
} from "@/constants/paying_companies";
import * as types from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import _ from "lodash";

/** Ou o CNPJ é escolhido na mão, ou é derivado dos contratos. Os dois escopos são exclusivos. */
const COMPANY_SCOPE = "company";
const CONTRACT_SCOPE = "contracts";

/**
 * A resolução é um contrato por requisição (ver `SEARCH_CONTRACTS_FOR_ADJUSTMENT`). O pool existe
 * para a planilha colada não virar 80 requisições simultâneas contra `contracts/search`, que ainda
 * calcula os totalizadores de status a cada chamada.
 */
const CONTRACT_LOOKUP_CONCURRENCY = 5;
const MAX_PASTED_CONTRACT_IDS = 200;

/** CPF tem 11 dígitos e id de contrato tem 5: o comprimento separa os dois sem perguntar. */
const CPF_DIGITS = 11;

export default {
  components: {
    // BSV
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormTextarea,
    BFormCheckbox,
    BCol,
    BRow,
    BFormInput,
    BFormRadioGroup,
    BFormRadio,
    BBadge,
    BSpinner,
    BInputGroup,
    BInputGroupAppend,
    BListGroup,
    BListGroupItem,
    vSelect,
    DynamicSelect,
    MoneyInput,
  },
  directives: {
    Ripple,
    money: VMoney,
  },
  data() {
    return {
      loading: {
        types: false,
        creditTypes: false,
        reasons: false,
        consultants: false,
        costCenters: false,
        costCenterCategories: false,
        contracts: false,
        contractSearch: false
      },
      saving: false,
      name: undefined,
      amount: undefined,
      type: undefined,
      launchDate: undefined,
      consultant: undefined,
      creditType: undefined,
      reason: undefined,
      hasRecurrence: false,
      recurrenceInMonths: "1",
      comment: undefined,
      costCenter: undefined,
      costCenterCategory: undefined,
      payingCompanyScope: COMPANY_SCOPE,
      payingCompany: undefined,
      referenceCompetence: undefined,
      contracts: [],
      contractIdsInput: "",
      contractSearchTerm: "",
      contractSearchResults: [],
      pasteFeedback: undefined
    };
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  validations() {
    return {
      name: { required },
      amount: {
        required,
        maxValue: function (value) {
          let amount = moneyToFloat(value);
          return amount > 0;
        },
      },
      type: { required },
      launchDate: { required },
      consultant: { required },
      creditType: { required },
      reason: {},
      hasRecurrence: {},
      recurrenceInMonths: {
        required,
        between: between(1, 12),
      },
      comment: {},
      costCenter: {},
      costCenterCategory: {
        required: requiredIf(() => {
          return this.costCenter
        }) 
      }
    };
  },
  computed: {
    ...mapGetters({
      saveFinancialAdjustmentSidebar: types.SAVE_FINANCIAL_ADJUSTMENT_SIDEBAR,
      types: types.FINANCIAL_ADJUSTMENT_TYPES,
      creditTypes: types.FINANCIAL_ADJUSTMENT_CREDIT_TYPES,
      reasons: types.FINANCIAL_ADJUSTMENT_REASONS,
      consultants: sharedTypes.CONSULTANTS,
      costCenters: sharedTypes.COST_CENTERS,
      costCenterCategories: sharedTypes.COST_CENTER_CATEGORIES
    }),
    moneyFormat: function () {
      return getBRLFormat();
    },
    isEdit: function () {
      return this.saveFinancialAdjustmentSidebar?.id
    },
    companyScope: function () {
      return COMPANY_SCOPE;
    },
    contractScope: function () {
      return CONTRACT_SCOPE;
    },
    payingCompanyOptions: function () {
      return PAYING_COMPANY_OPTIONS;
    },
    isContractScope: function () {
      return this.payingCompanyScope === CONTRACT_SCOPE;
    },
    // O primeiro contrato vinculado trava o CNPJ do lançamento. Os seguintes são conferidos contra
    // ele na entrada, e não no submit: recusar depois de montar a lista inteira joga fora o
    // trabalho de quem colou a planilha.
    lockedPayingCompanyId: function () {
      return this.contracts.length
        ? this.contracts[0].paying_company_id
        : undefined;
    },
    derivedPayingCompanyLabel: function () {
      return this.lockedPayingCompanyId
        ? payingCompanyLabel(this.lockedPayingCompanyId)
        : "Será definido pelo primeiro contrato vinculado";
    }
  },
  mounted() {
    this.loading.types = true;
    this.getFinancialAdjustmentTypes()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os tipos de lançamento. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.types = false;
      });
    this.loading.creditTypes = true;
    this.getCreditTypes()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar as formas de aplicação do lançamento. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.creditTypes = false;
      });
    this.loading.reasons = true;
    this.getFinancialAdjustmentReasons()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os motivos do lançamento. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.reasons = false;
      });
    this.loading.costCenters = true;
    this.getCostCenters()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os centros de custo. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.costCenters = false;
      });
   
  },
  methods: {
    floatToMoney,
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError);
    },
    ...mapMutations({
      mutateSaveFinancialAdjustmentSidebar:
        types.MUTATE_SAVE_FINANCIAL_ADJUSTMENT_SIDEBAR,
    }),
    ...mapActions({
      getFinancialAdjustmentReasons: types.GET_FINANCIAL_ADJUSTMENT_REASONS,
      getCreditTypes: types.GET_FINANCIAL_ADJUSTMENT_CREDIT_TYPES,
      getFinancialAdjustmentTypes: types.GET_FINANCIAL_ADJUSTMENT_TYPES,
      saveFinancialAdjustment: types.SAVE_FINANCIAL_ADJUSTMENT,
      getConsultants: sharedTypes.GET_CONSULTANTS,
      getFinancialAdjustment: types.GET_FINANCIAL_ADJUSTMENT,
      getCostCenters: sharedTypes.GET_COST_CENTERS,
      getCostCenterCategories: sharedTypes.GET_COST_CENTER_CATEGORIES,
      searchContractsForAdjustment: types.SEARCH_CONTRACTS_FOR_ADJUSTMENT
    }),
    onShow() {
      if (this.saveFinancialAdjustmentSidebar?.id) {
        this.getFinancialAdjustment(this.saveFinancialAdjustmentSidebar.id)
        .then((response) => {
            const { 
              name,
              value,
              type,
              launch_date,
              consultant_id,
              credit_type,
              financial_adjustment_reason_id,
              comment, 
              cost_center_id,
              cost_center_category_id,
              paying_company_id,
              reference_competence,
              contracts
            } = response.data;
            this.name = name;
            this.amount = floatToMoney(value);
            this.type = type;
            this.launchDate = launch_date;
            this.consultant = consultant_id;
            this.creditType = credit_type;
            this.reason = financial_adjustment_reason_id;
            this.comment = comment;
            this.costCenter = cost_center_id;
            this.costCenterCategory = cost_center_category_id;
            this.payingCompany = paying_company_id || undefined;
            this.referenceCompetence = reference_competence || undefined;
            this.contracts = (contracts || []).map((contract) => ({
              id: Number(contract.id),
              label: `#${contract.id}`,
              financial_solution_id: Number(contract.financial_solution_id),
              paying_company_id: payingCompanyFromFinancialSolutionId(
                contract.financial_solution_id
              )
            }));
            this.payingCompanyScope = this.contracts.length
              ? CONTRACT_SCOPE
              : COMPANY_SCOPE;
            this.enrichLinkedContractLabels();
            this.v$.$touch();
          })
          .catch(() => {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: "Ocorreu um erro ao carregar o lançamento para edição. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          });
        this.findConsultants()
        this.fetchCostCenterCategories()
      }
    },
    findConsultants(keyword) {
      this.loading.consultants = true;
      this.getConsultants({ keyword })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar os usuários para seleção. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading.consultants = false;
        });
    },
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;
      this.saving = true;
      const payload = {
        name: this.name,
        amount: moneyToFloat(this.amount),
        type: this.type,
        launch_date: this.launchDate,
        consultant_id: this.consultant,
        credit_type: this.creditType,
        recurrence_in_months: 1,
        reason: this.reason,
        comment: this.comment,
        cost_center_id: this.costCenter,
        cost_center_category_id: this.costCenterCategory,
        // Com contrato o CNPJ é do backend: ele re-deriva a partir de contract_ids e ignora o que
        // vier aqui. Mandar null deixa a intenção explícita em vez de mandar um valor morto.
        paying_company_id: this.isContractScope
          ? null
          : this.payingCompany || null,
        reference_competence: this.referenceCompetence || null,
        contract_ids: this.isContractScope
          ? this.contracts.map((contract) => contract.id)
          : []
      };
      /**
       * ⚠️ Só manda a recorrência se o switch estiver LIGADO.
       *
       * O campo some da tela pelo `v-if="!isEdit && hasRecurrence"`, mas o Vue 2 não zera o
       * `data` quando o `v-if` destrói o input, e não há watcher em `hasRecurrence` (só o
       * `clear()` do `@hidden` da sidebar restaura). Quem ligava o switch, digitava 12,
       * reconsiderava e desligava, salvava mesmo assim com `recurrence_in_months = 12` — e
       * `FinancialAdjustmentService::create` repete o valor CHEIO N vezes, sem dividir. Um ajuste
       * de R$ 1.079,28 virava R$ 12.951,36 sem a tela nunca ter mostrado o 12 no momento do save.
       */
      const recurrenceInMonths = parseInt(this.recurrenceInMonths);
      if (this.hasRecurrence && !isNaN(recurrenceInMonths)) {
        payload.recurrence_in_months = recurrenceInMonths;
      }
      if (this.saveFinancialAdjustmentSidebar?.id) {
        payload.id = this.saveFinancialAdjustmentSidebar?.id
      }
      this.saveFinancialAdjustment(payload)
        .then((response) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: "Lançamento salvo com sucesso!",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.saveFinancialAdjustmentSidebar.saveAction();
          this.mutateSaveFinancialAdjustmentSidebar({ visible: false });
        })
        .catch(err => {
          let message = "Ocorreu um erro ao salvar o lançamento. Entre em contato com o setor de TI.";
          const status = err.response?.status;
          const responseMessage = err.response?.data?.message;
          // O 400 do vínculo de contratos vem com o motivo escrito (CNPJs diferentes) e com a
          // instrução do que fazer. Trocar por texto genérico apaga a única coisa acionável que o
          // Financeiro recebe. O 400 de validação de campo não tem `message` e cai no genérico.
          if ((status == 400 || status == 405) && responseMessage) {
            message = responseMessage;
          }
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    costCenterChanged(){
      this.costCenterCategory = undefined
      this.fetchCostCenterCategories()
    },
    fetchCostCenterCategories(){
      if(!this.costCenter) return
      this.loading.costCenterCategories = true;
      this.getCostCenterCategories(this.costCenter)
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar as categorias de centro de custo. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading.costCenterCategories = false;
        });
    },
    payingCompanyName(id) {
      return payingCompanyLabel(id);
    },
    // ⚠️ Nesta tela success e danger já significam crédito e débito. O CNPJ usa secondary/info para
    // não ser lido como sinal do valor.
    payingCompanyVariant(id) {
      return Number(id) === PLATFORM_ID ? "light-info" : "light-secondary";
    },
    /** Nome sem número não é conferível contra a planilha; número sem nome não é conferível a olho. */
    contractLabel(row) {
      const number = row.proposal_number || row.id;
      const customer = row.customer_name || "Cliente não identificado";
      return row.financial_solution_name
        ? `#${number} — ${customer} (${row.financial_solution_name})`
        : `#${number} — ${customer}`;
    },
    toContractOption(row) {
      return {
        id: Number(row.id),
        label: this.contractLabel(row),
        financial_solution_id: Number(row.financial_solution_id),
        paying_company_id: payingCompanyFromFinancialSolutionId(
          row.financial_solution_id
        )
      };
    },
    /** Aceita vírgula, quebra de linha, ponto-e-vírgula ou espaço: o Financeiro cola da planilha. */
    parseContractIds(value) {
      const ids = (value || "")
        .split(/[^0-9]+/)
        .filter((piece) => piece !== "")
        .map(Number);
      return [...new Set(ids)];
    },
    // Falha de rede e "não existe" são coisas diferentes: tratar 403/500 como inexistente faria o
    // Financeiro apagar da planilha um contrato que existe.
    lookupContract(contractId) {
      return this.searchContractsForAdjustment({ contractId, limit: 1 })
        .then((response) => {
          const row = ((response.data || {}).data || [])[0];
          return { option: row ? this.toContractOption(row) : undefined };
        })
        .catch(() => ({ failed: true }));
    },
    async resolveContractIds(ids) {
      const queue = [...ids];
      const resolved = new Map();
      const workers = Array.from(
        { length: Math.min(CONTRACT_LOOKUP_CONCURRENCY, queue.length) },
        async () => {
          while (queue.length) {
            const contractId = queue.shift();
            resolved.set(contractId, await this.lookupContract(contractId));
          }
        }
      );
      await Promise.all(workers);
      return resolved;
    },
    async validatePastedContractIds() {
      const ids = this.parseContractIds(this.contractIdsInput);
      if (!ids.length) {
        this.pasteFeedback = undefined;
        return;
      }
      if (ids.length > MAX_PASTED_CONTRACT_IDS) {
        this.warn(
          `Cole no máximo ${MAX_PASTED_CONTRACT_IDS} contratos por vez. Vieram ${ids.length}.`
        );
        return;
      }
      this.loading.contracts = true;
      const resolved = await this.resolveContractIds(ids);
      const feedback = {
        linked: [],
        duplicated: [],
        missing: [],
        otherCompany: [],
        failed: []
      };
      // O travamento acompanha a lista dentro do próprio laço: se a colagem começar vazia, é o
      // primeiro id resolvido que fixa o CNPJ e os seguintes já são conferidos contra ele.
      let locked = this.lockedPayingCompanyId;
      ids.forEach((contractId) => {
        if (this.contracts.some((contract) => contract.id === contractId)) {
          feedback.duplicated.push(contractId);
          return;
        }
        const lookup = resolved.get(contractId) || {};
        if (lookup.failed) {
          feedback.failed.push(contractId);
          return;
        }
        if (!lookup.option) {
          feedback.missing.push(contractId);
          return;
        }
        if (locked && lookup.option.paying_company_id !== locked) {
          feedback.otherCompany.push(contractId);
          return;
        }
        this.contracts.push(lookup.option);
        locked = lookup.option.paying_company_id;
        feedback.linked.push(contractId);
      });
      this.pasteFeedback = feedback;
      // O que não entrou volta para a caixa: quem colou 40 ids não precisa reconstruir a lista dos
      // 3 que falharam.
      this.contractIdsInput = [
        ...feedback.missing,
        ...feedback.otherCompany,
        ...feedback.failed
      ].join(", ");
      this.loading.contracts = false;
    },
    searchContracts() {
      const digits = (this.contractSearchTerm || "").replace(/\D/g, "");
      if (!digits) {
        this.warn("Informe o ID do contrato ou o CPF do cliente.");
        return;
      }
      const payload =
        digits.length >= CPF_DIGITS
          ? { customerCPF: digits, limit: 10 }
          : { contractId: Number(digits), limit: 10 };
      this.loading.contractSearch = true;
      this.searchContractsForAdjustment(payload)
        .then((response) => {
          this.contractSearchResults = (
            (response.data || {}).data || []
          ).map((row) => this.toContractOption(row));
          if (!this.contractSearchResults.length) {
            this.warn("Nenhum contrato encontrado para esta busca.");
          }
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao buscar os contratos. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading.contractSearch = false;
        });
    },
    addContract(option) {
      if (this.contracts.some((contract) => contract.id === option.id)) {
        this.warn(`O contrato #${option.id} já está vinculado a este lançamento.`);
        return;
      }
      // Recusa na hora, com o motivo. Deixar entrar e só reprovar no submit devolveria um 400 seco
      // depois de o Financeiro ter montado a lista inteira.
      const locked = this.lockedPayingCompanyId;
      if (locked && option.paying_company_id !== locked) {
        this.warn(
          `O contrato #${option.id} é da ${payingCompanyLabel(
            option.paying_company_id
          )} e este lançamento já está travado na ${payingCompanyLabel(
            locked
          )}. Faça um lançamento separado para ele.`
        );
        return;
      }
      this.contracts.push(option);
    },
    removeContract(contractId) {
      this.contracts = this.contracts.filter(
        (contract) => contract.id !== contractId
      );
    },
    /**
     * A relação `contracts` do ajuste traz só id e solução financeira — dá para o CNPJ, não para o
     * rótulo. Os nomes vêm depois, e um contrato que não resolver MANTÉM o rótulo curto: descartá-lo
     * o desvincularia no próximo salvamento, porque a edição substitui a lista inteira.
     */
    async enrichLinkedContractLabels() {
      if (!this.contracts.length) return;
      const resolved = await this.resolveContractIds(
        this.contracts.map((contract) => contract.id)
      );
      this.contracts = this.contracts.map((contract) => {
        const lookup = resolved.get(contract.id) || {};
        return lookup.option
          ? { ...contract, label: lookup.option.label }
          : contract;
      });
    },
    // Trocar de escopo limpa o outro lado. Manter contratos vinculados com o escopo em "CNPJ"
    // mandaria contract_ids no submit e o backend derivaria um CNPJ que a tela não está mostrando.
    payingCompanyScopeChanged(scope) {
      if (scope === CONTRACT_SCOPE) {
        this.payingCompany = undefined;
        return;
      }
      this.contracts = [];
      this.contractSearchResults = [];
      this.contractIdsInput = "";
      this.pasteFeedback = undefined;
    },
    warn(text) {
      this.toast({
        component: ToastificationContent,
        props: {
          title: "Atenção!",
          text,
          icon: "AlertTriangleIcon",
          variant: "warning",
        },
      });
    },
    consultantChanged(consultant_id) {
      const consultant = _.find(this.consultants, { id: consultant_id });
      if (consultant && !consultant.receives_commission) {
        this.consultant = undefined
        this.v$.consultant.$touch()
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Atenção!",
            text: "O interessado selecionado é um colaborador que não deve receber ajustes financeiros. Por favor, crie uma despesa.",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
      }
    },

    clear() {
      this.name = undefined;
      this.amount = undefined;
      this.type = undefined;
      this.launchDate = undefined;
      this.consultant = undefined;
      this.creditType = undefined;
      this.reason = undefined;
      this.hasRecurrence = false;
      this.recurrenceInMonths = 1;
      this.comment = undefined;
      this.costCenter = undefined;
      this.costCenterCategory = undefined;
      this.payingCompanyScope = COMPANY_SCOPE;
      this.payingCompany = undefined;
      this.referenceCompetence = undefined;
      this.contracts = [];
      this.contractIdsInput = "";
      this.contractSearchTerm = "";
      this.contractSearchResults = [];
      this.pasteFeedback = undefined;
      this.mutateSaveFinancialAdjustmentSidebar({ id: undefined });
      this.v$.$reset();
    },
  },
};
</script>
